/**
 * @fileoverview B-SCR-008 Monthly Summary Screen
 * @description View monthly attendance summaries per crew member
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { get, getToken } from '../services/api';
import MasterLinkButton from '../components/MasterLinkButton';
import { Download, FileText } from 'lucide-react';
import { useSort, usePager, SortableTh } from '../components/TableTools';
import { hasFeature } from '../utils/featureFlags';

interface MonthlySummary {
  id: string; user_id: string; user_name: string; vessel_id: string; vessel_name: string;
  target_year: number; target_month: number; total_work_days: number;
  total_work_minutes: number; total_overtime_minutes: number;
  total_break_minutes: number; total_night_minutes: number;
  /** 労働区分（機能フラグ labor_categories）: 上限判定用／非常作業68条／安全臨時労働64条／労働外 */
  total_limit_minutes?: number; total_exempt_minutes?: number; total_safety_ot_minutes?: number; total_nonwork_minutes?: number;
  nonwork_breakdown?: Record<string, number>; meal_auto_minutes?: number;
  average_daily_work_minutes: number; max_consecutive_work_days: number;
}

interface ApiRes { data: MonthlySummary[]; meta: { total_count: number; total_pages: number; page: number } }

const fmtMin = (m: number) => m != null && m > 0 ? `${Math.floor(m / 60)}h${m % 60}m` : '-';

function toCsvRow(cols: string[]): string {
  return cols.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',');
}

function downloadCsv(summaries: MonthlySummary[], year: number, month: number) {
  const header = ['氏名', '船名', '出勤日数', '総勤務(分)', '残業(分)', '休憩(分)', '日平均(分)', '最大連勤(日)'];
  const rows = summaries.map(s => [
    s.user_name,
    s.vessel_name,
    String(s.total_work_days),
    String(s.total_work_minutes),
    String(s.total_overtime_minutes),
    String(s.total_break_minutes),
    String(s.average_daily_work_minutes),
    String(s.max_consecutive_work_days),
  ]);
  const csv = [toCsvRow(header), ...rows.map(toCsvRow)].join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `月次集計_${year}年${month}月.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const MonthlyPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [summaries, setSummaries] = useState<MonthlySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear]   = useState(() => Number(searchParams.get('year'))  || new Date().getFullYear());
  const [month, setMonth] = useState(() => Number(searchParams.get('month')) || new Date().getMonth() + 1);
  const [csvLoading, setCsvLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  // サマリーチップのドリルダウン（該当者リスト表示）
  const [drillDown, setDrillDown] = useState<'overtime' | 'consecutive' | null>(null);

  useEffect(() => {
    setSearchParams({ year: String(year), month: String(month) }, { replace: true });
  }, [year, month, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get<ApiRes>('/monthly-summaries', {
        page: '1', per_page: '500',
        target_year: String(year), target_month: String(month),
      });
      setSummaries(res.data ?? []);
    } catch (err) { console.error('[MonthlyPage]', err); }
    setLoading(false);
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  // ソート → ページング（共通部品）
  const sort = useSort<MonthlySummary>(summaries, {
    user_name:                  s => s.user_name,
    vessel_name:                s => s.vessel_name,
    total_work_days:            s => s.total_work_days,
    total_work_minutes:         s => s.total_work_minutes,
    total_overtime_minutes:     s => s.total_overtime_minutes,
    total_break_minutes:        s => s.total_break_minutes,
    average_daily_work_minutes: s => s.average_daily_work_minutes,
    max_consecutive_work_days:  s => s.max_consecutive_work_days,
  }, 'user_name');
  const pager = usePager(sort.sorted, { noun: '名' });

  // CSV用: 全件取得
  const handleCsvDownload = async () => {
    setCsvLoading(true);
    try {
      const res = await get<ApiRes>('/monthly-summaries', {
        page: '1', per_page: '500',
        target_year: String(year), target_month: String(month),
      });
      downloadCsv(res.data ?? [], year, month);
    } catch (err) { console.error('[MonthlyPage]', err); }
    setCsvLoading(false);
  };

  const handlePdfDownload = async () => {
    setPdfLoading(true);
    try {
      const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001/api';
      const subdomain = (() => { const h = window.location.hostname; if (h !== 'localhost' && h !== '127.0.0.1') { const p = h.split('.'); if (p.length >= 3 && p[0]) return p[0]; } return (import.meta.env.VITE_COMPANY_SUBDOMAIN as string | undefined) ?? 'unison-tech'; })();
      const token = getToken() ?? '';
      const url = `${apiBase}/monthly-summaries/pdf?target_year=${year}&target_month=${month}`;
      const r = await fetch(url, { headers: { 'X-Company-Subdomain': subdomain, 'X-App-Type': 'admin', Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('PDF生成失敗');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `月次集計_${year}年${month}月.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) { console.error('[MonthlyPage/pdf]', err); alert('PDF出力に失敗しました'); }
    setPdfLoading(false);
  };

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <h1 className="page-title">月次集計</h1>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <MasterLinkButton tab="overtime" title="残業ルール(集計の基準)を開きます" />
          <button className="btn-o" onClick={handleCsvDownload} disabled={csvLoading}>
            {csvLoading ? '…' : <><Download size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />CSV</>}
          </button>
          <button className="btn-o" onClick={handlePdfDownload} disabled={pdfLoading} style={{ background: '#dc2626', color: 'white', borderColor: '#dc2626' }}>
            {pdfLoading ? '生成中…' : <><FileText size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />PDF出力</>}
          </button>
        </div>
      </div>

      <div className="flex-row" style={{ marginBottom: 16 }}>
        <div className="form-g" style={{ margin: 0 }}>
          <label className="form-l">年</label>
          <select className="form-i" value={year} onChange={e => setYear(Number(e.target.value))}>
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => <option key={y} value={y}>{y}年</option>)}
          </select>
        </div>
        <div className="form-g" style={{ margin: 0 }}>
          <label className="form-l">月</label>
          <select className="form-i" value={month} onChange={e => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}月</option>)}
          </select>
        </div>
      </div>

      {/* サマリーバー */}
      {!loading && summaries.length > 0 && (() => {
        const totalOt  = summaries.reduce((s, r) => s + r.total_overtime_minutes, 0);
        const warnCount = summaries.filter(s => s.total_overtime_minutes >= 3600).length;
        const consWarn  = summaries.filter(s => s.max_consecutive_work_days > 14).length;
        return (
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ background: '#fff', borderRadius: 10, padding: '10px 18px', border: '1px solid #e2e8f0', fontSize: 13 }}>
              <span style={{ color: '#64748b' }}>対象人数: </span><strong>{summaries.length}名</strong>
            </div>
            <div style={{ background: '#fff', borderRadius: 10, padding: '10px 18px', border: '1px solid #e2e8f0', fontSize: 13 }}>
              <span style={{ color: '#64748b' }}>総残業: </span>
              <strong style={{ color: totalOt >= 3600 ? '#dc2626' : '#0f172a' }}>{fmtMin(totalOt)}</strong>
            </div>
            {warnCount > 0 && (
              <button onClick={() => setDrillDown('overtime')} title="クリックで該当者を表示"
                style={{ background: '#fef2f2', borderRadius: 10, padding: '10px 18px', border: '1px solid #fecaca', fontSize: 13, color: '#dc2626', fontWeight: 700, cursor: 'pointer' }}>
                残業60h超: {warnCount}名 ▸
              </button>
            )}
            {consWarn > 0 && (
              <button onClick={() => setDrillDown('consecutive')} title="クリックで該当者を表示"
                style={{ background: '#fffbeb', borderRadius: 10, padding: '10px 18px', border: '1px solid #fde68a', fontSize: 13, color: '#d97706', fontWeight: 700, cursor: 'pointer' }}>
                連続勤務14日超: {consWarn}名 ▸
              </button>
            )}
          </div>
        );
      })()}

      {/* サマリーチップのドリルダウン: 該当者一覧 */}
      {drillDown && (() => {
        const isOt = drillDown === 'overtime';
        const hits = summaries
          .filter(s => isOt ? s.total_overtime_minutes >= 3600 : s.max_consecutive_work_days > 14)
          .sort((a, b) => isOt
            ? b.total_overtime_minutes - a.total_overtime_minutes
            : b.max_consecutive_work_days - a.max_consecutive_work_days);
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={() => setDrillDown(null)}>
            <div style={{ background: '#fff', borderRadius: 14, width: 460, maxWidth: '92vw', maxHeight: '75vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: isOt ? '#dc2626' : '#d97706' }}>
                  {isOt ? '残業60時間超の乗組員' : '連続勤務14日超の乗組員'}
                </span>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{year}年{month}月・{hits.length}名</span>
                <button onClick={() => setDrillDown(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18 }}>✕</button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px 22px 18px' }}>
                {hits.map(s => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 9, background: isOt ? '#fef2f2' : '#fffbeb', border: `1px solid ${isOt ? '#fecaca' : '#fde68a'}`, marginBottom: 6 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{s.user_name}</div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>{s.vessel_name || '-'}・出勤{s.total_work_days}日</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: isOt ? '#dc2626' : '#d97706' }}>
                        {isOt ? fmtMin(s.total_overtime_minutes) : `${s.max_consecutive_work_days}日連続`}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{isOt ? '月間残業' : '最大連勤'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {loading ? <div className="loading"><div className="spinner" /></div> : (
        <>
        {pager.renderBar('top')}
        <div className="card" style={{ padding: 0, overflow: 'auto' }}>
          <table>
            <thead>
              <tr>
                <SortableTh label="船員" k="user_name" sort={sort} />
                <SortableTh label="船舶" k="vessel_name" sort={sort} />
                <SortableTh label="出勤日数" k="total_work_days" sort={sort} />
                <th>勤務時間バー</th>
                <SortableTh label="総勤務" k="total_work_minutes" sort={sort} />
                <SortableTh label="残業" k="total_overtime_minutes" sort={sort} />
                <SortableTh label="休憩" k="total_break_minutes" sort={sort} />
                {hasFeature('labor_categories') && <th style={{ whiteSpace: 'nowrap' }} title="非常作業（68条）／安全臨時労働（64条）">別枠</th>}
                {hasFeature('labor_categories') && <th style={{ whiteSpace: 'nowrap' }} title="労働時間に含めない記録（食事など）">労働外</th>}
                <SortableTh label="日平均" k="average_daily_work_minutes" sort={sort} />
                <SortableTh label="最大連勤" k="max_consecutive_work_days" sort={sort} />
              </tr>
            </thead>
            <tbody>
              {pager.pageRows.length === 0 ? (
                <tr><td colSpan={9} className="empty">データがありません</td></tr>
              ) : pager.pageRows.map(s => {
                const otHigh    = s.total_overtime_minutes >= 3600;   // 60h超
                const otMed     = s.total_overtime_minutes >= 1800 && !otHigh; // 30h超
                const consHigh  = s.max_consecutive_work_days > 14;
                const rowBg     = otHigh ? '#fef2f2' : otMed ? '#fff7ed' : undefined;
                // 勤務時間バー: 月160h (9600min) を最大とする
                const barPct    = Math.min(100, Math.round(s.total_work_minutes / 9600 * 100));
                const otPct     = Math.min(100, Math.round(s.total_overtime_minutes / 9600 * 100));
                return (
                  <tr key={s.id} style={{ background: rowBg }}>
                    <td style={{ fontWeight: 600 }}>
                      {otHigh && <span title="残業60h超" style={{ marginRight: 4, color: '#ef4444' }}>!</span>}
                      {s.user_name}
                    </td>
                    <td>{s.vessel_name}</td>
                    <td>{s.total_work_days}日</td>
                    <td style={{ minWidth: 100 }}>
                      <div style={{ position: 'relative', height: 10, background: '#f1f5f9', borderRadius: 5, overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${barPct}%`, background: otHigh ? '#ef4444' : otMed ? '#f97316' : '#3b82f6', borderRadius: 5, transition: 'width 0.5s' }} />
                        {otPct > 0 && (
                          <div style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: `${otPct}%`, background: 'rgba(239,68,68,0.3)', borderRadius: 5 }} />
                        )}
                      </div>
                    </td>
                    <td>{fmtMin(s.total_work_minutes)}</td>
                    <td style={{ color: otHigh ? '#dc2626' : otMed ? '#f97316' : undefined, fontWeight: otHigh ? 700 : undefined }}>
                      {fmtMin(s.total_overtime_minutes)}
                    </td>
                    <td>{fmtMin(s.total_break_minutes)}</td>
                    {hasFeature('labor_categories') && (
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12 }} title="非常作業（68条・上限計算から除外）／安全臨時労働（64条・上限超えが合法）">
                        {((s.total_exempt_minutes ?? 0) > 0 || (s.total_safety_ot_minutes ?? 0) > 0)
                          ? <span style={{ color: '#b91c1c', fontWeight: 700 }}>
                              {(s.total_exempt_minutes ?? 0) > 0 && `非常 ${fmtMin(s.total_exempt_minutes ?? 0)}`}
                              {(s.total_exempt_minutes ?? 0) > 0 && (s.total_safety_ot_minutes ?? 0) > 0 && ' / '}
                              {(s.total_safety_ot_minutes ?? 0) > 0 && `安全臨時 ${fmtMin(s.total_safety_ot_minutes ?? 0)}`}
                            </span>
                          : <span style={{ color: '#94a3b8' }}>-</span>}
                      </td>
                    )}
                    {hasFeature('labor_categories') && (
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12 }} title={Object.entries(s.nonwork_breakdown ?? {}).map(([k, v]) => `${k}: ${fmtMin(v)}`).join(' / ') || '労働時間に含めない記録（食事など）'}>
                        {(s.total_nonwork_minutes ?? 0) > 0
                          ? <span style={{ color: '#64748b', fontWeight: 700 }}>{fmtMin(s.total_nonwork_minutes ?? 0)}{(s.meal_auto_minutes ?? 0) > 0 && <span style={{ fontSize: 10, marginLeft: 4 }}>（食事自動控除 {fmtMin(s.meal_auto_minutes ?? 0)}）</span>}</span>
                          : <span style={{ color: '#94a3b8' }}>-</span>}
                      </td>
                    )}
                    <td>{fmtMin(s.average_daily_work_minutes)}</td>
                    <td style={{ color: consHigh ? '#dc2626' : undefined, fontWeight: consHigh ? 700 : undefined }}>
                      {s.max_consecutive_work_days}日
                      {consHigh && <span title="連続勤務14日超" style={{ marginLeft: 4, fontSize: 12, color: '#dc2626' }}>!</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pager.renderBar('bottom')}
        </>
      )}

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-h">月次集計について</div>
        <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.8 }}>
          <p>当月の完了済み勤怠セッションを集計しています。</p>
          <p>残業基準: 1日8時間（480分）超過分。連続勤務14日超はアラート対象。</p>
        </div>
      </div>
    </div>
  );
};

export default MonthlyPage;
